/**
 * One camera form for Plan and the live "Add to shoot" sheet: Manned · Static · Aerial.
 *  Manned  follows its operator (default: me) — or is run by someone without the app (a typed name,
 *          e.g. "Kofi (photographer)", placed Here / on the map / at a place); focal / sensor optional.
 *  Static  "Use my phone here" → position + compass heading, or on the map / at a place; focal +
 *          sensor required (FOV). Optionally who runs it (app user or a name).
 *  Aerial  on a mission aircraft (default: the Chase); focal optional.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { bearingDeg, cameraFov, crewPeople, distanceM, fovDeg, isZoom, PRIME_PRESETS, SENSOR_PRESETS, ZOOM_PRESETS, type Camera, type LatLon, type MissionBundle } from '@apos/shared';
import { Button, Chip, Field, Segmented, Select, TextArea, TextInput } from '../../components/ui';
import { atLeast } from '../../app/labels';
import { api, type CameraInput } from '../../lib/api';
import { fovText, nextCameraLabel } from '../plan/planModel';
import { PhoneAim } from './PhoneAim';
import { useMapPick, WherePicker } from './WherePicker';
import {
  blankCamera, CAMERA_TYPE_OPTIONS, cameraBody, normDeg, cameraPatch, cameraType, lensKind, readLastCamera, rememberCamera, ROLE_LABEL, sensorPresetId,
  validateCamera, withCameraType, FACING_REQUIRED_MSG, SENSOR_REQUIRED_MSG, ZOOM_RANGE_MSG, type CameraType, type LensKind,
} from './setupModel';
import type { Mutate } from './mutate';
import './setup.css';
import { Disclosure, FormSection, InlineError } from './FormBits';

export interface CameraFormProps {
  bundle: MissionBundle;
  value: Camera | 'new';
  initialType?: CameraType;
  /** New camera at a known spot (e.g. "Static camera here" from a place) — and that place's id. */
  initialPosition?: LatLon | null;
  initialLocationId?: string | null;
  mutate: Mutate;
  /** After save (the saved camera) or remove (null). */
  onDone(saved: Camera | null): void;
  onCancel(): void;
  /** Lay out the fields and the action row (e.g. inside a Modal body / footer). */
  render(body: ReactNode, footer: ReactNode): ReactNode;
}

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(/[^\d.]/g, '')) || null);

export function CameraForm({ bundle, value, initialType = 'manned', initialPosition = null, initialLocationId = null, mutate, onDone, onCancel, render }: CameraFormProps) {
  const isNew = value === 'new';
  const me = bundle.me.userId;
  const canEditAll = atLeast(bundle.myRole, 'producer');
  const ctx = useMemo(() => ({ meUserId: me, aircraft: bundle.aircraft, last: readLastCamera() }), [me, bundle.aircraft]);
  const fallbackLabel = useMemo(() => nextCameraLabel(bundle.cameras.map((c) => c.label)), [bundle.cameras]);
  const [f, setF] = useState<CameraInput>(() =>
    value === 'new' ? { ...blankCamera(fallbackLabel, initialType, ctx), position: initialPosition, locationId: initialPosition ? initialLocationId : null } : { ...value, operatorName: value.operatorName ?? null },
  );
  const pick = useMapPick(bundle);
  /** A new spot; `locationId` when it is one of the mission's places (the camera then follows it). */
  const setSpot = (p: LatLon, locationId: string | null = null) => setF((x) => ({ ...x, position: p, locationId }));
  const [preset, setPreset] = useState(() => sensorPresetId(f));
  const [lens, setLens] = useState<LensKind>(() => lensKind(f));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof CameraInput>(k: K, v: CameraInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const type = cameraType(f.mount);
  // the one validation message is shown next to what it is about
  const errAt = !err ? null : err === SENSOR_REQUIRED_MSG || err === ZOOM_RANGE_MSG ? 'lens' : err === FACING_REQUIRED_MSG || /Set where the camera is/.test(err) ? 'where' : /who runs/.test(err) ? 'operator' : /aircraft this camera/.test(err) ? 'aircraft' : 'form';
  const errHere = (at: string) => (errAt === at && err ? <InlineError>{err}</InlineError> : null);
  const zoom = lens === 'zoom' && isZoom(f);
  // a zoom shows its FOV at both ends ("HFOV 27.0°–10.3°"); the text line uses the long end
  const fov = fovText(zoom ? { ...f, focalLengthMm: f.focalMaxMm! } : f);
  const hfov = cameraFov(zoom ? { ...f, focalLengthMm: f.focalMaxMm! } : f);
  const hfovWide = zoom && f.sensorWidthMm ? fovDeg(f.sensorWidthMm, f.focalMinMm!) : null;
  const changeLens = (k: LensKind) => {
    setLens(k);
    // prime ← zoom keeps the long end as the focal; zoom ← prime starts from a preset around it
    if (k === 'prime') setF((x) => ({ ...x, focalLengthMm: x.focalMaxMm ?? x.focalLengthMm, focalMinMm: null, focalMaxMm: null }));
    else setF((x) => {
      const p = ZOOM_PRESETS.find((z) => x.focalLengthMm != null && x.focalLengthMm >= z.minMm && x.focalLengthMm <= z.maxMm) ?? ZOOM_PRESETS[1]!;
      return { ...x, focalMinMm: p.minMm, focalMaxMm: p.maxMm, focalLengthMm: p.maxMm };
    });
  };

  const changeType = (t: CameraType) => {
    const next = withCameraType(f, t, ctx);
    setF(next);
    setPreset(sensorPresetId(next));
    setErr(null);
  };

  const save = async () => {
    const problem = validateCamera(f);
    if (problem) return setErr(problem);
    setErr(null);
    const body = cameraBody(f, fallbackLabel);
    setBusy(true);
    const r = await mutate('save the camera', () => {
      if (value === 'new') return api.cameras.create(bundle.mission.id, body);
      const patch = cameraPatch(value, body, canEditAll);
      return Object.keys(patch).length ? api.cameras.update(bundle.mission.id, value.id, patch) : Promise.resolve(value);
    });
    setBusy(false);
    if (!r) return;
    if (type === 'static') rememberCamera(body);
    onDone(r);
  };
  const remove = async () => {
    if (value === 'new') return;
    const r = await mutate('remove the camera', () => api.cameras.remove(bundle.mission.id, value.id).then(() => true));
    if (r) onDone(null);
  };

  const fovChip =
    fov && hfov ? (
      <Chip size="sm" tone="strong">
        HFOV {hfovWide != null ? `${hfovWide.toFixed(1)}°–` : ''}
        {hfov.hfovDeg.toFixed(1)}°
      </Chip>
    ) : null;
  const body = (
    <div className="setup-form stack-16">
      <Segmented
        label="Camera type"
        block
        size="lg"
        value={type}
        onChange={changeType}
        options={CAMERA_TYPE_OPTIONS.map((o) => ({ ...o, disabled: !canEditAll && o.value !== type }))}
      />

      {type === 'manned' ? (
        <FormSection title="Who and where">
          <OperatorField bundle={bundle} f={f} setF={setF} required disabled={!canEditAll} />
          {errHere('operator')}
          {!f.operatorUserId ? (
            <WherePicker
              bundle={bundle}
              label="Where are they?"
              value={f.position}
              onChange={(p, how) => setSpot(p, how.locationId)}
              onPickOnMap={() => pick.start('Tap where they are filming from', (p) => setSpot(p))}
              disabled={!canEditAll}
            />
          ) : null}
        </FormSection>
      ) : null}

      {type === 'static' ? (
        <FormSection title="Position and facing">
          <PhoneAim
            position={f.position}
            headingDeg={f.headingDeg}
            onPosition={(p) => setSpot(p)}
            onHeading={(h) => set('headingDeg', h)}
            existing={!isNew && !!f.position}
          />
          <WherePicker
            bundle={bundle}
            label="Or set the spot"
            hideHere
            hideValue
            value={f.position}
            onChange={(p, how) => setSpot(p, how.locationId)}
            onPickOnMap={() => pick.start('Tap where the camera is', (p) => setSpot(p))}
          />
          {f.position ? <FaceToward bundle={bundle} from={f.position} self={f.locationId ?? null} onHeading={(h) => set('headingDeg', h)} /> : null}
          {f.headingDeg === null && errAt !== 'where' ? (
            <p className="setup-hint setup-hint--warn" role="status">
              No field of view until you set which way it faces.
            </p>
          ) : null}
          {errHere('where')}
        </FormSection>
      ) : null}
      {type === 'static' && canEditAll ? (
        <FormSection title="Operator">
          <OperatorField bundle={bundle} f={f} setF={setF} />
        </FormSection>
      ) : null}

      {type === 'aerial' ? (
        <FormSection title="On board">
          <Field label="Aircraft" error={errAt === 'aircraft' ? err : undefined} hint={bundle.aircraft.length ? undefined : 'Add the aircraft first (Aircraft tab).'}>
            {(id) => (
              <Select id={id} value={f.aircraftId ?? ''} onChange={(e) => { setErr(null); set('aircraftId', e.target.value || null); }} disabled={!canEditAll}>
                <option value="">Choose…</option>
                {bundle.aircraft.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} · {ROLE_LABEL[a.role]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </FormSection>
      ) : null}

      <details open={type === 'static' || errAt === 'lens'}><summary>Lens and sensor{type === 'static' ? ' · needed for field of view' : ' · optional'}</summary>
      <FormSection title="Lens and sensor" aside={fovChip}>
        <div className="setup-lens" data-testid="camera-lens">
          <Segmented
            label="Lens"
            block
            value={lens}
            onChange={changeLens}
            options={[
              { value: 'prime', label: 'Prime' },
              { value: 'zoom', label: 'Zoom' },
            ]}
          />
          {lens === 'prime' ? (
            <>
              <Segmented
                label="Common primes"
                block
                size="sm"
                className="setup-lens__presets num"
                value={f.focalLengthMm != null ? String(f.focalLengthMm) : ''}
                onChange={(v) => { setErr(null); set('focalLengthMm', Number(v)); }}
                options={PRIME_PRESETS.map((mm) => ({ value: String(mm), label: String(mm) }))}
              />
              <Field label="Focal length (mm)" optional={type !== 'static'}>
                {(id) => <TextInput id={id} mono inputMode="decimal" aria-invalid={errAt === 'lens' && !f.focalLengthMm ? true : undefined} value={f.focalLengthMm ?? ''} onChange={(e) => { setErr(null); set('focalLengthMm', num(e.target.value)); }} placeholder="50" />}
              </Field>
            </>
          ) : (
            <>
              <Segmented
                label="Common zooms"
                block
                size="sm"
                className="setup-lens__presets num"
                value={isZoom(f) ? `${f.focalMinMm}-${f.focalMaxMm}` : ''}
                onChange={(v) => {
                  const [lo, hi] = v.split('-').map(Number);
                  setErr(null);
                  setF((x) => ({ ...x, focalMinMm: lo!, focalMaxMm: hi!, focalLengthMm: hi! }));
                }}
                options={ZOOM_PRESETS.map((z) => ({ value: `${z.minMm}-${z.maxMm}`, label: `${z.minMm}–${z.maxMm}` }))}
              />
              <div className="form-row">
                <Field label="Wide (mm)" optional={type !== 'static'}>
                  {(id) => <TextInput id={id} mono inputMode="decimal" value={f.focalMinMm ?? ''} onChange={(e) => { setErr(null); set('focalMinMm', num(e.target.value)); }} placeholder="70" />}
                </Field>
                <Field label="Long (mm)" optional={type !== 'static'}>
                  {(id) => <TextInput id={id} mono inputMode="decimal" value={f.focalMaxMm ?? ''} onChange={(e) => { setErr(null); setF((x) => ({ ...x, focalMaxMm: num(e.target.value), focalLengthMm: num(e.target.value) })); }} placeholder="200" />}
                </Field>
              </div>
            </>
          )}
        </div>
        <Field label="Sensor" optional={type !== 'static'}>
          {(id) => (
            <Select
              id={id}
              value={preset}
              disabled={!canEditAll}
              aria-invalid={errAt === 'lens' && !f.sensorWidthMm ? true : undefined}
              onChange={(e) => {
                setErr(null);
                setPreset(e.target.value);
                const p = SENSOR_PRESETS.find((x) => x.id === e.target.value);
                if (p) setF((x) => ({ ...x, sensorWidthMm: p.widthMm, sensorHeightMm: p.heightMm }));
                else if (!e.target.value) setF((x) => ({ ...x, sensorWidthMm: null, sensorHeightMm: null }));
              }}
            >
              <option value="">Choose sensor…</option>
              {SENSOR_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom size…</option>
            </Select>
          )}
        </Field>
        {preset === 'custom' ? (
          <div className="form-row">
            <Field label="Sensor width (mm)">
              {(id) => <TextInput id={id} mono inputMode="decimal" value={f.sensorWidthMm ?? ''} onChange={(e) => set('sensorWidthMm', num(e.target.value))} />}
            </Field>
            <Field label="Sensor height (mm)">
              {(id) => <TextInput id={id} mono inputMode="decimal" value={f.sensorHeightMm ?? ''} onChange={(e) => set('sensorHeightMm', num(e.target.value))} />}
            </Field>
          </div>
        ) : null}
        <div className="plan-fov setup-fov num" aria-live="polite" data-testid="camera-fov">
          {fov && hfov ? (
            <>
              {fovChip} {zoom ? `wide ${Math.round(f.focalMinMm!)} mm → long ${Math.round(f.focalMaxMm!)} mm` : fov}
            </>
          ) : (
            <span className="muted">HFOV —{type === 'static' ? (lens === 'zoom' ? ' · needs the zoom range + sensor' : ' · needs focal length + sensor') : ''}</span>
          )}
        </div>
        {errHere('lens')}
      </FormSection>
      </details>

      <FormSection title="Name">
        <div className="form-row">
          <Field label="Label">{(id) => <TextInput id={id} value={f.label} onChange={(e) => set('label', e.target.value)} disabled={!canEditAll} />}</Field>
          <Field label="Body" optional>
            {(id) => <TextInput id={id} value={f.body ?? ''} onChange={(e) => set('body', e.target.value || null)} placeholder="ARRI ALEXA 35" disabled={!canEditAll} />}
          </Field>
        </div>
        <Disclosure title="Lens and notes" hint={[f.lens, f.notes ? 'notes' : null].filter(Boolean).join(' · ')}>
          <div className="stack-16">
            <Field label="Lens" optional>
              {(id) => <TextInput id={id} value={f.lens ?? ''} onChange={(e) => set('lens', e.target.value || null)} placeholder="Fujinon 25-300" disabled={!canEditAll} />}
            </Field>
            <Field label="Notes" optional>
              {(id) => <TextArea id={id} rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} disabled={!canEditAll} />}
            </Field>
          </div>
        </Disclosure>
      </FormSection>
      {errHere('form')}
    </div>
  );

  const footer = (
    <div className="setup-foot">
      {!isNew && canEditAll ? (
        <Button variant="danger" icon="trash" onClick={() => void remove()}>
          Remove
        </Button>
      ) : null}
      <span className="setup-foot__spacer" />
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={() => void save()} loading={busy}>
        {isNew ? 'Add camera' : 'Save camera'}
      </Button>
    </div>
  );

  if (pick.overlay) return <>{pick.overlay}</>;
  return <>{render(body, footer)}</>;
}

const NO_APP = '__name__';
/** Option value prefix for someone on the call sheet without the app (crew lane). */
const PERSON = '__person__:';

/** "Face toward" an airport or a place: sets the facing from the camera's spot (no compass needed). */
function FaceToward({ bundle, from, self, onHeading }: { bundle: MissionBundle; from: LatLon; self: string | null; onHeading(h: number): void }) {
  const targets = [
    ...bundle.missionAirports.flatMap((m) => {
      const a = bundle.airports.find((x) => x.icao === m.icao);
      return a ? [{ id: `apt:${a.icao}`, label: `${a.icao} (airport)`, at: { lat: a.lat, lon: a.lon } }] : [];
    }),
    ...bundle.locations.filter((l) => l.id !== self).map((l) => ({ id: `loc:${l.id}`, label: l.name, at: { lat: l.lat, lon: l.lon } })),
  ].filter((t) => distanceM(from, t.at) > 30);
  if (!targets.length) return null;
  return (
    <Select
      aria-label="Face toward"
      value=""
      onChange={(e) => {
        const t = targets.find((x) => x.id === e.target.value);
        if (t) onHeading(normDeg(bearingDeg(from, t.at)));
      }}
    >
      <option value="">Face toward…</option>
      {targets.map((t) => (
        <option key={t.id} value={t.id}>
          {t.label}
        </option>
      ))}
    </Select>
  );
}

/**
 * Who runs the camera: a crew member with the app (their live position) or someone without it
 * (type a name). Manned requires one; Static may be unmanned.
 */
function OperatorField({
  bundle,
  f,
  setF,
  required,
  disabled,
}: {
  bundle: MissionBundle;
  f: CameraInput;
  setF(fn: (x: CameraInput) => CameraInput): void;
  required?: boolean;
  disabled?: boolean;
}) {
  const me = bundle.me.userId;
  // crew lane: people on the call sheet without the app are picked by name (the server links the person)
  const noApp = useMemo(() => crewPeople(bundle).filter((p) => !p.userId), [bundle]);
  const known = (n: string | null | undefined) => (n?.trim() ? noApp.find((p) => p.name.toLowerCase() === n.trim().toLowerCase()) : undefined);
  const [typing, setTyping] = useState(() => !f.operatorUserId && !!f.operatorName && !known(f.operatorName));
  const sel = f.operatorUserId ?? (typing ? NO_APP : known(f.operatorName) ? `${PERSON}${known(f.operatorName)!.name}` : '');
  return (
    <>
      <Field
        label={required ? 'Who runs it' : 'Who runs it (optional)'}
        hint={f.operatorUserId ? (required ? 'The camera goes wherever they are (their shared location).' : 'They can re-aim it from their phone.') : undefined}
      >
        {(id) => (
          <Select
            id={id}
            value={sel}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              setTyping(v === NO_APP);
              if (v.startsWith(PERSON)) return setF((x) => ({ ...x, operatorUserId: null, operatorName: v.slice(PERSON.length) }));
              setF((x) => ({ ...x, operatorUserId: v && v !== NO_APP ? v : null, operatorName: v === NO_APP ? (known(x.operatorName) ? '' : x.operatorName) : null }));
            }}
          >
            {required ? <option value="">Choose…</option> : <option value="">Nobody / unmanned</option>}
            {bundle.members
              .filter((m) => !m.isSimulated || m.userId === f.operatorUserId)
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.callsign || m.displayName}
                  {m.userId === me ? ' (me)' : ''}
                </option>
              ))}
            {noApp.map((p) => (
              <option key={p.id} value={`${PERSON}${p.name}`}>
                {p.name} (no app)
              </option>
            ))}
            <option value={NO_APP}>Someone without the app…</option>
          </Select>
        )}
      </Field>
      {sel === NO_APP ? (
        <Field label="Their name" hint="They don’t need the app — the camera stays where you put it.">
          {(id) => (
            <TextInput
              id={id}
              autoFocus
              value={f.operatorName ?? ''}
              disabled={disabled}
              maxLength={60}
              onChange={(e) => setF((x) => ({ ...x, operatorName: e.target.value }))}
              placeholder="Kofi (photographer)"
            />
          )}
        </Field>
      ) : null}
    </>
  );
}
